import { useMemo, useState } from 'react';
import { Container, Nav, Navbar } from 'react-bootstrap';
import {
  useClinicianProfile,
  useDashboardTopBar,
  useLogout,
} from '../../hooks';
import './styles/customised-navbar.css';
import { useLocation, useNavigate } from 'react-router-dom';
// @ts-ignore
import BootstrapSelect from 'react-bootstrap-select-dropdown';
import { dropdownStyle } from '../../../auth';
import useUserPermission from '../../hooks/useUserPermission';
import { useDispatch, useSelector } from 'react-redux';
import {
  resetCurrentFilter,
  setCurrentFilter,
  setFromNavBar,
} from '../../../store/ai2';
import { useTriggerUpdate } from '../../../context';
import type { RootState } from '../../../store';
import { Brand } from './components/Brand';
import AddPatientModal from '../AddPatientModal';
import AddPatientSetupModal from '../AddNewPatient/AddTenancyPatientModal';
import { CustomisedNavbarMenuItems } from './components/CustomosiedNavbar';
import { DataSourceTimestamp } from './components/DataSourceTimestamp';

//// assets
import automedicLogo from '../../../assets/img/auto-medic/automedic-logo.png';
import endozoneLogo from '../../../assets/img/endozone/endozone-logo.png';
import baseLogo from '../../../assets/img/brandImage.png';
import SearchLogo from '../../../assets/img/button-icons/search.svg';
import NotificationLogo from '../../../assets/img/button-icons/notification.svg';
import ProfileLogo from '../../../assets/img/button-icons/profile.svg';
import SettingsLogo from '../../../assets/img/button-icons/settings.svg';
import GraphLogo from '../../../assets/img/button-icons/graph.svg';
import SignoutLogo from '../../../assets/img/button-icons/sign-out.svg';
import exit from '../../../assets/img/exit.png';

const logoMapper: Record<string, string> = {
  'automedic-logo': automedicLogo,
  'endozone-logo': endozoneLogo,
  'base-logo': baseLogo,
};

const iconMapper: Record<string, string> = {
  'fa-search': SearchLogo,
  'fa-bell': NotificationLogo,
  'fa-user': ProfileLogo,
  'fa-cog': SettingsLogo,
  'fa-bar-chart': GraphLogo,
  'fa-sign-out': SignoutLogo,
};

export interface CustomisedNavbarProps {
  disabledAction?: boolean;
}

export const CustomisedNavbar = ({
  disabledAction = false,
}: CustomisedNavbarProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { resetHaveMoreData } = useTriggerUpdate();
  const dispatch = useDispatch();
  const { userHasPermissions } = useUserPermission();
  const { menuItems, logoSettings, dataSource, lastImportBySource } =
    useDashboardTopBar();
  const {
    firstName,
    tenancies,
    selectedTenancy,
    tenancyOptions,
    updateTenancy,
  } = useClinicianProfile();
  const { userSavedFilters, savedFiltersInitialized } = useSelector(
    (state: RootState) => state.dashboardSearch,
  );
  const cannotGoToDashboard =
    userSavedFilters.length === 0 &&
    savedFiltersInitialized &&
    !!selectedTenancy?.features?.searchFiltersMap;

  const { logout } = useLogout();
  const [showAddPatient, setShowAddPatient] = useState(false);

  const addPatientModal = useMemo(() => {
    const supportsAddPatientSetup =
      selectedTenancy?.features &&
      selectedTenancy.features.addPatientSetup?.active;
    if (supportsAddPatientSetup) {
      return (
        <AddPatientSetupModal
          open={showAddPatient}
          onSetOpen={setShowAddPatient}
        />
      );
    }
    return (
      <div>
        <AddPatientModal
          toChild={showAddPatient}
          sendToParent={setShowAddPatient}
        />
      </div>
    );
  }, [selectedTenancy, showAddPatient]);

  const checkNavItemVisibility = (item: any) => {
    if (!item.needPermission) {
      return true;
    }
    const mode = Object.keys(item.needPermission)[0] as 'or' | 'and';
    const permissions = item.needPermission[mode];
    return userHasPermissions(permissions, mode);
  };

  const filteredMenuItems = menuItems.filter(checkNavItemVisibility);

  const openAddPatientModal = () => setShowAddPatient(true);

  const isAddNewPatientModalItem = (item: any) =>
    item.type === 'link' &&
    item.displayType === 'modal' &&
    item.id === 'add-new-patient';

  const handleLinkClick = (item: any) => {
    if (isAddNewPatientModalItem(item)) {
      openAddPatientModal();
      return;
    }

    if (location.pathname === '/chat' && item.url === '/chat') {
      window.dispatchEvent(new CustomEvent('chat-home:request-end-chat'));
      return;
    }

    const isDashboardUrl = item.url?.includes?.('dashboard');
    const isAllPatientsEntry =
      isDashboardUrl &&
      typeof item.label === 'string' &&
      item.label.toLowerCase().includes('all patient');
    if (item.url?.includes?.('dashboard')) {
      if (cannotGoToDashboard) {
        navigate('/search');
        return;
      }
      resetHaveMoreData(true);
    }
    if (isAllPatientsEntry) {
      dispatch(setFromNavBar(false));
      dispatch(setCurrentFilter(['All Patients']));
      navigate(item.url);
      return;
    }
    if (isDashboardUrl) {
      dispatch(resetCurrentFilter());
    }
    dispatch(setFromNavBar(true));
    navigate(item.url);
  };

  return (
    <div>
      <Navbar
        id="topNav"
        expand="md"
        fixed="top"
        className="customised-nav-bar-container"
      >
        <Container fluid>
          <div className="navbar-left-section">
            <Brand
              onClick={() => navigate('/')}
              logoSrc={logoMapper[logoSettings?.type || 'base-logo']}
              logoAlt={logoSettings?.alt || 'logo'}
              clickable={true}
              showText={!!logoSettings?.logoText}
              text={logoSettings?.logoText}
            />

            <DataSourceTimestamp
              dataSource={dataSource}
              lastImportBySource={lastImportBySource}
            />
          </div>

          <Navbar.Toggle aria-controls="basic-navbar-nav" />

          <Navbar.Collapse className="justify-content-end">
            <Nav className="navbar-container">
              {tenancies.length > 1 && (
                <Nav.Item>
                  <BootstrapSelect
                    className="nav-item nav-link tenancy-toggle"
                    style={dropdownStyle}
                    options={tenancyOptions}
                    placeholder={selectedTenancy?.tenancy_name}
                    onChange={updateTenancy}
                    showTick={true}
                    disabled={disabledAction}
                  />
                </Nav.Item>
              )}
              <CustomisedNavbarMenuItems
                items={filteredMenuItems}
                disabledAction={disabledAction}
                firstName={firstName}
                iconMapper={iconMapper}
                checkNavItemVisibility={checkNavItemVisibility}
                onLinkClick={handleLinkClick}
                navigate={navigate}
                logout={logout}
                exitIconSrc={exit}
              />
            </Nav>
          </Navbar.Collapse>
        </Container>
      </Navbar>
      {showAddPatient && addPatientModal}
    </div>
  );
};
